import { useState, type FormEvent } from "react"
import { createPortal } from "react-dom"
import { Link } from "react-router-dom"
import { X, Send, MessageCircle, ArrowRight, BookOpen, Radio } from "lucide-react"
import { useModalOpen } from "../lib/useModalOpen"
import { useBranding } from "../lib/useBranding"
import { useAuth } from "./AuthProvider"
import { RichTextEditor } from "./RichTextEditor"

/** Support email for all contact form submissions (platform standard). */
const SUPPORT_EMAIL = "support@luster.company"

/** Legacy Mintlify URL; show in-app /docs link instead. */
const LEGACY_DOCS_URL = "https://docs.luster.company"

function getContactRecipientCopy(role: string | null): string {
  switch (role) {
    case "client":
      return "Your message goes to Luster support and will be answered from support@luster.company."
    case "admin":
      return "Your message goes to Luster support (support@luster.company) for account and product help."
    case "operator":
      return "Your message goes to Luster support (support@luster.company) for field and app support."
    case "creator":
      return "Your message goes to platform support (support@luster.company)."
    default:
      return "Your message will be sent to support@luster.company and we'll respond as soon as we can."
  }
}

export type ContactUsModalProps = {
  onClose: () => void
}

export function ContactUsModal({ onClose }: ContactUsModalProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  useModalOpen(true)
  const { role } = useAuth()
  const { branding } = useBranding()
  const recipientCopy = getContactRecipientCopy(role)
  let helpCenterUrl =
    branding?.docsUrl?.trim() ||
    (import.meta.env.VITE_HELP_CENTER_URL as string | undefined)?.trim() ||
    "/docs"
  if (
    helpCenterUrl &&
    (helpCenterUrl === LEGACY_DOCS_URL || helpCenterUrl.startsWith(LEGACY_DOCS_URL + "/"))
  ) {
    helpCenterUrl = "/docs"
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || sent) return
    setSubmitting(true)
    // Placeholder: in production would POST to your backend or email service
    await new Promise((r) => setTimeout(r, 600))
    setSent(true)
    setSubmitting(false)
  }

  const content = (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="contact-us-title"
        className="fixed inset-0 z-[60] flex flex-col overflow-hidden sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-xl sm:rounded-3xl bg-stone-950/95 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] sm:animate-in sm:fade-in sm:zoom-in-95 sm:duration-200"
      >
        {/* Header — 404/Manifest terminal glass */}
        <div className="flex items-center justify-between shrink-0 px-6 pt-7 pb-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-400/20 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h2 id="contact-us-title" className="text-[11px] font-mono font-bold uppercase tracking-widest text-white/90">
                Contact us
              </h2>
              <p className="text-[10px] font-mono text-white/40 mt-0.5">Support · support@luster.company</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          <p className="text-[11px] font-mono text-white/40 mb-4 leading-relaxed" aria-live="polite">
            {recipientCopy}
          </p>
          <div className="mb-5 p-4 rounded-xl border border-white/[0.08] bg-white/[0.04]">
            <p className="text-[11px] font-mono text-white/50 mb-3 leading-snug">
              Curious about your own portal? Get facility intelligence that actually shines.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link
                to="/apply"
                onClick={onClose}
                className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-wider text-violet-400 hover:text-violet-300 transition-colors"
              >
                Request access
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/trace"
                onClick={onClose}
                className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-wider text-amber-400 hover:text-amber-300 transition-colors"
              >
                Platform Trace
                <Radio className="w-3.5 h-3.5" />
              </Link>
              {helpCenterUrl &&
                (helpCenterUrl.startsWith("/") ? (
                  <Link
                    to={helpCenterUrl}
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-wider text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    Help center
                    <BookOpen className="w-3.5 h-3.5" />
                  </Link>
                ) : (
                  <a
                    href={helpCenterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-wider text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    Help center
                    <BookOpen className="w-3.5 h-3.5" />
                  </a>
                ))}
            </div>
          </div>
          {sent ? (
            <div className="py-8 text-center">
              <div className="rounded-xl border border-white/[0.08] bg-black/40 px-4 py-3 mb-4 inline-block">
                <p className="font-mono text-[10px] text-white/30 uppercase tracking-widest mb-0.5">0x06</p>
                <p className="text-[11px] font-mono font-bold text-white/90">Message sent</p>
              </div>
              <p className="text-[11px] font-mono text-white/40 mb-6">
                We&apos;ll get back to you as soon as we can.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl border border-white/[0.08] bg-white/[0.06] text-[11px] font-mono font-bold uppercase tracking-wider text-white/80 hover:bg-white/[0.1] transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="contact-name" className="block text-[10px] font-mono font-bold text-white/50 uppercase tracking-widest mb-1.5">
                  Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 font-mono text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 focus:border-teal-400/20 transition-all"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-[10px] font-mono font-bold text-white/50 uppercase tracking-widest mb-1.5">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 font-mono text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 focus:border-teal-400/20 transition-all"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-[10px] font-mono font-bold text-white/50 uppercase tracking-widest mb-1.5">
                  Message
                </label>
                <RichTextEditor
                  content={message}
                  onChange={setMessage}
                  placeholder="How can we help?"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl border border-white/[0.08] bg-white/[0.04] text-[11px] font-mono font-bold uppercase tracking-wider text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-teal-500/20 border border-teal-400/25 text-teal-400 text-[11px] font-mono font-bold uppercase tracking-wider hover:bg-teal-500/30 transition-colors disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <span className="animate-pulse">Sending…</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  )

  return createPortal(content, document.body)
}
